import React, { FC } from "react";
import { useNavigate } from "react-router";
import { Button, Icon, InputSearch, Tooltip } from "@shared/components";
// Type-only -- elided by babel before module resolution runs, so this alias needs no jest mock
// (BUILD CONSTRAINT recipe: only VALUE imports through @containers/@shared aliases need one).
import { RecognitionCounts } from "@containers/Recognition/interfaces";

import "./index.scss";

export interface RecognitionsHeaderProps {
  onCreateRecognition: () => void;
  // Required, unlike searchValue/onChangeSearch below -- the badge shows on EVERY tab (it is not an
  // Overview-only affordance), so every caller has one real value to pass, never an "unwired" state.
  recognitionCounts: RecognitionCounts;
  // Optional: only the Overview tab wires these (page-header-promotion-plan.md §"Why cross-tab
  // search is not in this PR"). Every other tab still renders the search box — it is simply
  // unwired there, per the same plan's scope note.
  searchValue?: string;
  onChangeSearch?: (search: string) => void;
  // all-recognitions-screen-plan.md "The header": one optional OBJECT, not two independent optionals
  // (`backTo` alone would permit a label with nowhere to go; `label` alone, a back arrow with no
  // trail -- two states that should not be representable). Absent, as in every caller before this
  // plan, the header renders exactly as before. Present (the dedicated All Recognitions screen),
  // "Recognitions" demotes to the muted parent crumb and `label` becomes the leaf.
  breadcrumb?: { backTo: string; label: string };
}

/**
 * The page header sitting above the Recognitions tab bar (rewards-shell-restructure-plan.md §"The
 * page header"). Originally held only the CTA that used to repeat on every TabBarItem via TabBar's
 * actionButton* slot (RecognitionsTabBarContainer.tsx's now-removed `giveRecognitionButtonProps`).
 *
 * page-header-promotion-plan.md promotes the title and search field up into this component from
 * `RecognitionListContainer/RecognitionListHeader`, which rendered them one level down on the
 * Overview tab only. Both are stripped out of that component in the same change (its own file has the
 * detail) so the title and search box each appear exactly once.
 *
 * The count badge (received + sent) is built here too. It deliberately does NOT read from
 * `receivedRecognitionsTotal`/`sentRecognitionsTotal` (the received/sent LIST slice) — those are
 * zeroed by `clearRecognitions` on `RecognitionListContainer`'s unmount, and this header persists
 * across every tab, so that source would show a correct number on Overview and 0 everywhere else.
 * `recognitionCounts` instead comes from its own dedicated fetch (`getRecognitionCounts`, mirroring
 * the Review tab's `getPendingCount`/`pendingCount` precedent), which `clearRecognitions` leaves alone.
 * The SUM is computed here, in the presentational component, so it stays testable with two directly
 * injected numbers rather than depending on a container's own arithmetic.
 *
 * The click handler for the CTA (the showOverlay dispatch) stays in the container — this component is
 * presentational only. Same for the search: the value/handler are passed in by the caller, which
 * decides whether and how they connect to real filter state.
 *
 * all-recognitions-screen-plan.md adds the optional `breadcrumb`: when given, the "Recognitions"
 * title demotes to a muted parent crumb, a back arrow (navigating to `breadcrumb.backTo`, never
 * `navigate(-1)` — a deep link or a refresh has no history entry to go back to) and a rotated
 * chevron separator are added, and `breadcrumb.label` becomes the leaf in the title's own place. This
 * is the one place `useNavigate` appears in an otherwise dispatch-free component — a router hook, not
 * a redux one, so the "presentational" claim above still holds in the sense that matters here (no
 * dispatch/select of its own).
 */
const RecognitionsHeader: FC<RecognitionsHeaderProps> = (props) => {
  const { onCreateRecognition, recognitionCounts, searchValue, onChangeSearch, breadcrumb } = props;
  const navigate = useNavigate();

  // ONE handler for both breadcrumb controls below (the back arrow and the "Recognitions" parent
  // crumb): they go to the same place for the same reason, and two inline copies of the same call
  // would be free to drift apart.
  //
  // `replace` rather than push: pushing grows a new history entry per round trip, so browser Back from
  // the Overview would land right back on this screen instead of wherever the viewer actually came
  // from. And `breadcrumb.backTo`, never `navigate(-1)` -- a deep link or a refresh has no history
  // entry to go back to.
  const goToBreadcrumbTarget = breadcrumb ? () => navigate(breadcrumb.backTo, { replace: true }) : undefined;

  return (
    <div className="recognitions-header">
      <div className="recognitions-header-title-group">
        {breadcrumb ? (
          <div className="recognitions-header-breadcrumb">
            {/* A real <button>, not an onClick on Icon: Icon renders a bare <div> (Icon.tsx), so as an
                Icon this was unreachable by keyboard and had no accessible name -- and on this screen it is
                the ONLY way out, because the dedicated route deliberately has no section tab bar. */}
            <Tooltip text="Back to Recognitions">
              <button
                type="button"
                className="recognitions-header-back"
                aria-label="Back to Recognitions"
                onClick={goToBreadcrumbTarget}
              >
                <Icon type="back" />
              </button>
            </Tooltip>
            {/* A real <button>, not an onClick on a span -- same reasoning as the back arrow directly
                above: a bare <span onClick> has no role and no tab stop, so it was unreachable by
                keyboard and had no accessible name. Same handler too, so the crumb and the arrow cannot
                disagree about where "back" is. */}
            <button type="button" className="recognitions-header-breadcrumb-parent" onClick={goToBreadcrumbTarget}>
              Recognitions
            </button>
            <Icon type="chevron-left" className="recognitions-header-breadcrumb-separator" />
            <span className="recognitions-header-title">{breadcrumb.label}</span>
          </div>
        ) : (
          <div className="recognitions-header-title">Recognitions</div>
        )}
        <div className="recognitions-header-count-badge" data-testid="recognitions-header-count-badge">
          {recognitionCounts.received + recognitionCounts.sent}
        </div>
      </div>
      <div className="recognitions-header-actions">
        <InputSearch
          id="recognitions-header-search"
          placeholder="Search recognition..."
          onChangeSearch={onChangeSearch ?? (() => undefined)}
          value={searchValue}
        />
        <Button className="accent padding new-recognition-button" onClick={onCreateRecognition}>
          {/* The "+" is real text, not a CSS ::before glyph: a pseudo-element's generated content can
              leak into a button's accessible name in some browser/AT combinations, so plain text is
              the deliberate a11y choice. That TECHNIQUE carries over from the button this replaces —
              the label itself changed ("+ Give recognition" → "+ New Recognition"), so it is not a
              verbatim port. */}
          + New Recognition
        </Button>
      </div>
    </div>
  );
};

export default RecognitionsHeader;
